// Hook: manages the active POS order draft, creating one lazily on first item add.
import { useState, useCallback, useRef, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPatch, apiDelete, apiGet } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/store";

export interface OrderItem {
  id: string;
  orderId: string;
  variantId: string;
  quantity: number;
  unitPrice: string;
  discount: string;
  lineTotal: string;
  note: string;
  sortOrder: number;
  productName?: string | null;
  sku?: string | null;
  /** True when the variant is serial-tracked. Commit requires exactly quantity serials. */
  serialTracked?: boolean;
  serials?: Array<{ id: string; serialNo: string }>;
}

/** Một khoản trả trong đơn. Nhiều khoản = thanh toán hỗn hợp (tiền mặt + CK). */
export interface PaymentPart {
  method: "cash" | "bank_manual";
  amount: number;
}

export interface Order {
  id: string;
  code: string;
  status: string;
  subtotal: string;
  discountAmount: string;
  couponAmount: string;
  couponCode?: string | null;
  total: string;
  paidAmount: string;
  debtAmount: string;
  paymentMethod?: string | null;
  /** Tên NV bán hàng gõ tay — in lên hóa đơn (khác tài khoản thu ngân). */
  salesperson?: string;
  cashierEmail?: string | null;
  orderType?: string;
  items?: OrderItem[];
  customerId?: string | null;
  createdAt: string;
  paidAt?: string | null;
  voidedAt?: string | null;
}

export function usePosCart() {
  const [order, setOrder] = useState<Order | null>(null);
  // Desired order type for the NEXT draft created (before any item/draft exists).
  // Once a draft exists, the order's own order_type is authoritative.
  const [pendingOrderType, setPendingOrderType] = useState<string>("retail");
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);

  // Selling warehouse (kho bán) for the NEXT draft. Set by the sales screen's
  // warehouse dropdown (defaults to the retail/wholesale kho per channel).
  const [warehouseId, setWarehouseId] = useState<string | null>(null);

  // In-memory cache: variantId → first imageUrl (persists for the session lifetime).
  const imageCache = useRef<Record<string, string>>({});

  const cacheVariantImage = useCallback((variantId: string, imageUrl?: string) => {
    if (imageUrl) imageCache.current[variantId] = imageUrl;
  }, []);

  // Track closed (hidden) draft IDs — persisted in localStorage so they survive refresh.
  const [hiddenDraftIds, setHiddenDraftIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("pos_hidden_drafts");
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch { return new Set(); }
  });

  // Poll for current cashier's draft orders (refreshed every 30s).
  const { data: rawDrafts = [] } = useQuery({
    queryKey: ["orders", "drafts", user?.id],
    queryFn: () =>
      apiGet<Order[]>("/v1/orders", {
        status: "draft",
        cashier_id: user?.id,
        limit: 10,
      }),
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const drafts = useMemo(
    () => rawDrafts.filter((d) => !hiddenDraftIds.has(d.id)),
    [rawDrafts, hiddenDraftIds],
  );

  // Close (hide) a draft tab — hidden client-side only (no server delete for drafts).
  const closeDraft = useCallback((orderId: string) => {
    setHiddenDraftIds((prev) => {
      const next = new Set(prev).add(orderId);
      localStorage.setItem("pos_hidden_drafts", JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Create a new draft order and return it. The selling warehouse may be passed
  // explicitly (warehouseIdArg) or taken from the screen's current selection
  // (warehouseId state). When neither is set the backend falls back to the
  // default kho.
  const createDraft = useCallback(async (warehouseIdArg?: string, orderType?: string): Promise<Order> => {
    const body: Record<string, string> = { orderType: orderType ?? pendingOrderType };
    const wh = warehouseIdArg ?? warehouseId ?? undefined;
    if (wh) body.warehouseId = wh;

    const o = await apiPost<Order>("/v1/orders", body);
    setOrder(o);
    qc.invalidateQueries({ queryKey: ["orders", "drafts"] });
    return o;
  }, [qc, pendingOrderType, warehouseId]);

  // Ensure a draft exists, creating one if needed. Returns order id.
  const ensureDraft = useCallback(async (): Promise<string> => {
    if (order) return order.id;
    const o = await createDraft(undefined, pendingOrderType);
    return o.id;
  }, [order, createDraft, pendingOrderType]);

  // Switch retail/wholesale. With an active draft this PATCHes the order (server
  // re-prices lines); without one it just records the choice for the next draft.
  const setOrderType = useCallback(async (orderType: string) => {
    if (!order) {
      setPendingOrderType(orderType);
      return;
    }
    const o = await apiPatch<Order>(`/v1/orders/${order.id}`, { orderType });
    setOrder(o);
  }, [order]);

  // Add a variant to the current order (auto-creates draft if needed).
  // orderId may be passed explicitly to target a freshly-created draft whose
  // setOrder() has not yet committed to React state — avoids a second draft
  // and a stale-closure mismatch during the serial-first scan chain.
  const addItem = useMutation({
    mutationFn: async ({ variantId, quantity, orderId }: { variantId: string; quantity?: number; orderId?: string }) => {
      const oid = orderId ?? (await ensureDraft());
      return apiPost<Order>(`/v1/orders/${oid}/items`, { variantId, quantity: quantity ?? 1 });
    },
    onSuccess: (o) => setOrder(o),
  });

  // Update a line item (qty / price / discount / serials / note).
  const updateItem = useMutation({
    mutationFn: async ({
      itemId,
      quantity,
      unitPrice,
      discount,
      serialIds,
      note,
      orderId,
    }: {
      itemId: string;
      quantity?: number;
      unitPrice?: string;
      discount?: string;
      serialIds?: string[];
      note?: string;
      orderId?: string;
    }) => {
      // Prefer an explicit orderId (e.g. the just-created draft from a serial
      // scan) over `order` state, which may be stale within the same tick.
      const oid = orderId ?? order?.id;
      if (!oid) throw new Error("no active order");
      return apiPatch<Order>(`/v1/orders/${oid}/items/${itemId}`, {
        quantity,
        unitPrice,
        discount,
        serialIds,
        note,
      });
    },
    onSuccess: (o) => setOrder(o),
  });

  // Remove a line item.
  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      if (!order) throw new Error("no active order");
      return apiDelete(`/v1/orders/${order.id}/items/${itemId}`).then(
        () => apiGet<Order>(`/v1/orders/${order.id}`),
      );
    },
    onSuccess: (o) => setOrder(o as Order),
  });

  // Patch order header (discount, customer, note, salesperson).
  const patchHeader = useMutation({
    mutationFn: async (patch: { discountAmount?: string; customerId?: string; note?: string; salesperson?: string }) => {
      if (!order) throw new Error("no active order");
      return apiPatch<Order>(`/v1/orders/${order.id}`, patch);
    },
    onSuccess: (o) => setOrder(o),
  });

  // Commit the order (cash payment, optional coupon, optional reason for partial-pay).
  // onError is intentionally omitted here so callers can provide their own handler
  // (e.g. the payment panel shows a toast with translateApiError so the cashier
  // knows which serial is missing/sold and can fix before retrying).
  const commit = useMutation({
    mutationFn: async ({
      paymentMethod,
      tenderedAmount,
      payments,
      couponCode,
      reason,
    }: {
      paymentMethod: string;
      tenderedAmount: number;
      /** Nhiều khoản trả trong 1 đơn (tiền mặt + chuyển khoản). Có thì BE bỏ qua
       *  paymentMethod/tenderedAmount. */
      payments?: PaymentPart[];
      couponCode?: string;
      reason?: string;
    }) => {
      if (!order) throw new Error("no active order");
      return apiPost<Order>(`/v1/orders/${order.id}/commit`, {
        paymentMethod,
        tenderedAmount,
        ...(payments && payments.length > 0 ? { payments } : {}),
        ...(couponCode ? { couponCode } : {}),
        ...(reason ? { reason } : {}),
      });
    },
    onSuccess: (o) => {
      setOrder(o);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
    // No onError here: caller owns error display so they can show context-aware messages
    // (e.g. "cần 1 serial, đã gắn 0" prompts the cashier to scan, not just a generic alert).
  });

  // Hard-delete a draft order server-side (manager/admin only — enforced by BE).
  // Used by the POS tab close action for privileged users to truly purge an
  // abandoned cart instead of just hiding it client-side.
  const deleteDraft = useMutation({
    mutationFn: (orderId: string) => apiDelete(`/v1/orders/${orderId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", "drafts"] });
    },
  });

  // Resume an existing draft order by ID.
  const resumeDraft = useCallback(async (orderId: string) => {
    const o = await apiGet<Order>(`/v1/orders/${orderId}`);
    setOrder(o);
  }, []);

  // Clear cart (start new order). Resets the pending order type to retail.
  const clearCart = useCallback(() => {
    setOrder(null);
    setPendingOrderType("retail");
  }, []);

  // Active order type: the order's own value once a draft exists, else the pending choice.
  const orderType = order?.orderType ?? pendingOrderType;

  return {
    order,
    drafts,
    addItem,
    updateItem,
    removeItem,
    patchHeader,
    commit,
    createDraft,
    ensureDraft,
    closeDraft,
    deleteDraft,
    resumeDraft,
    clearCart,
    orderType,
    setOrderType,
    imageCache: imageCache.current,
    cacheVariantImage,
    /** Selling warehouse for new drafts — set by the sales screen's kho dropdown. */
    warehouseId,
    setWarehouseId,
  };
}
